import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { StoryImage } from "../data/story";
type CarouselProps = {
  images: StoryImage[];
  label: string;
  aspect?: string;
  autoPlay?: boolean;
  showCaption?: boolean;
  fit?: "cover" | "contain";
  frameClassName?: string;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function Carousel({
  images,
  label,
  aspect = "aspect-[4/5]",
  autoPlay = true,
  showCaption = true,
  fit = "cover",
  frameClassName = "bg-coal"
}: CarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const go = useCallback((delta: number) => setIndex(i => (i + delta + images.length) % images.length), [images.length]);
  useEffect(() => {
    if (!autoPlay || paused || reduce || images.length < 2) return;
    const id = window.setInterval(() => go(1), 5000);
    return () => window.clearInterval(id);
  }, [autoPlay, paused, reduce, go, images.length]);
  const current = images[index];
  return <div className="relative" role="region" aria-roledescription="carrusel" aria-label={label} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-1" data-mp="2qzwnwi7ugou3">
      <div className={`relative overflow-hidden rounded-2xl ${frameClassName} ${aspect}`} data-id="element-2" data-mp="1yck1c81ooh0s">
        <AnimatePresence initial={false} data-id="element-3" data-mp="23ac09m0qx3eh">
          <motion.img key={current.src + index} src={current.src} alt={current.alt} initial={{
          opacity: 0,
          scale: reduce ? 1 : 1.02
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.28,
          ease
        }} className={`absolute inset-0 h-full w-full select-none ${fit === "contain" ? "object-contain" : "object-cover"}`} draggable={false} data-id="element-4" data-mp="23b3ogbs0jyuq" />
        </AnimatePresence>
        {showCaption && <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-ink/70 px-4 py-3" data-id="element-5" data-mp="2hyp5mpblo4n8">
            <p className="text-sm text-paper" data-id="element-6" data-mp="2wnlhyykow3tn">{current.alt}</p>
          </div>}
      </div>

      {images.length > 1 && <div className="mt-4 flex items-center justify-between gap-4" data-id="element-7" data-mp="2ro8p9x7502j5">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Seleccionar foto" data-id="element-8" data-mp="23yo2f37v4l4l">
            {images.map((img, i) => <button key={img.src + i} role="tab" aria-selected={i === index} aria-label={`Foto ${i + 1} de ${images.length}`} onClick={() => setIndex(i)} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? "w-8 bg-accent" : "w-3 bg-line hover:bg-mute"}`} data-id="element-9" data-mp="1z0uuo5qwvj27" data-mpi={i} />)}
          </div>
          <div className="flex shrink-0 gap-2" data-id="element-10" data-mp="3rwfbhe1355tk">
            <button onClick={() => go(-1)} aria-label="Foto anterior" className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-paper transition-colors duration-150 ease-out hover:border-paper" data-id="element-11" data-mp="3ryruluikxm87">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-12" data-mp="3mxudll9b2y8a" />
            </button>
            <button onClick={() => go(1)} aria-label="Foto siguiente" className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-paper transition-colors duration-150 ease-out hover:border-paper" data-id="element-13" data-mp="eituvhvyh1x8">
              <ChevronRightIcon className="h-5 w-5" data-id="element-14" data-mp="9qahakx9q6sf" />
            </button>
          </div>
        </div>}
    </div>;
}